// AdminGoogleCloudPanel — Feb 20, 2026 (Randy, Plan B)
//
// Super-admin-only. Sits at the top of the Go-Live Wizard. This is where
// the admin does the Google Cloud Console dance ONCE and every My Silo
// user thereafter connects their Google Calendar through the platform's
// shared OAuth client — no more per-user Cloud Console setup.
//
// The mobile-specific callouts (JavaScript-origins skip, iPhone eye icon,
// missed-the-secret recovery, Branding-vs-Audience) all live here so
// they're front-and-center for whoever runs this wizard.

import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import {
  ArrowSquareOut, Copy, FloppyDisk, GoogleLogo, CheckCircle,
  WarningCircle, Eye, DeviceMobile, ShieldCheck, Info, CaretDown, CaretUp,
} from "@phosphor-icons/react";

const REDIRECT_PATH = "/api/oauth/calendar/callback";

// A single step in the collapsible walkthrough. Everything under here is
// intentionally terse — the whole guide fits on one screen when collapsed.
function Step({ num, title, done, children }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`border rounded-lg overflow-hidden ${done ? "border-[#4A5D4E]/40 bg-[#EAF0EB]/40" : "border-[#E5E4E2] bg-white"}`} data-testid={`admin-gcal-step-${num}`}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full px-4 py-3 flex items-center gap-3 text-left hover:bg-[#F0F1EE]/50"
        data-testid={`admin-gcal-step-toggle-${num}`}
      >
        <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium shrink-0 ${done ? "bg-[#4A5D4E] text-white" : "bg-[#F0F1EE] text-[#2C2B2A]"}`}>
          {done ? "✓" : num}
        </div>
        <div className="flex-1 text-sm font-medium text-[#1C1B1A]">{title}</div>
        {open ? <CaretUp size={14} className="text-[#2C2B2A]" /> : <CaretDown size={14} className="text-[#2C2B2A]" />}
      </button>
      {open && <div className="px-4 pb-4 pt-1 text-xs text-[#1C1B1A] leading-relaxed space-y-2 border-t hairline">{children}</div>}
    </div>
  );
}

function Ext({ href, children }) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[#4A5D4E] hover:underline break-all">
      {children} <ArrowSquareOut size={11} weight="bold" />
    </a>
  );
}

export default function AdminGoogleCloudPanel() {
  const [cid, setCid] = useState("");
  const [secret, setSecret] = useState("");
  const [status, setStatus] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [collapsed, setCollapsed] = useState(false);

  const redirectUri = `${window.location.origin}${REDIRECT_PATH}`;

  const load = async () => {
    try {
      const r = await api.get("/calendar/admin/google/shared");
      setStatus(r.data);
    } catch (e) { /* not admin — parent hides component */ }
  };

  useEffect(() => { load(); }, []);

  const copy = async (text, msg = "Copied.") => {
    try { await navigator.clipboard.writeText(text); toast.success(msg); }
    catch { toast.error("Couldn't copy."); }
  };

  const save = async () => {
    setSaving(true);
    setTestResult(null);
    try {
      await api.post("/calendar/admin/google/shared", { client_id: cid.trim(), client_secret: secret.trim() });
      toast.success("Shared Google OAuth client saved. Hit Test next.");
      setCid(""); setSecret("");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't save."); }
    finally { setSaving(false); }
  };

  const test = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const r = await api.post("/calendar/admin/google/shared/test", { origin: window.location.origin });
      setTestResult(r.data);
      if (r.data.ok) toast.success("Google says the shared client is ready.");
      else toast.error("Test failed — see details below.", { duration: 6000 });
    } catch (e) {
      setTestResult({ ok: false, error: e.response?.data?.detail || "Test failed — try again." });
    } finally { setTesting(false); }
  };

  const remove = async () => {
    if (!window.confirm("Remove the shared Google OAuth client? All users will lose their calendar connections until it's set up again.")) return;
    try {
      await api.post("/calendar/admin/google/shared", { clear: true });
      setTestResult(null);
      await load();
      toast.success("Shared Google client cleared.");
    } catch { toast.error("Couldn't clear."); }
  };

  if (!status) return null; // still loading — or not admin (endpoint 403s)

  const configured = status.has_credentials;

  return (
    <section className="border-2 border-[#4A5D4E]/30 rounded-xl bg-[#FDFCF8] overflow-hidden" data-testid="admin-google-cloud-panel">
      <header className="px-5 py-4 bg-gradient-to-br from-[#EAF0EB] to-[#F5F1E8] border-b hairline flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <GoogleLogo size={24} weight="duotone" className="text-[#4A5D4E] shrink-0" />
          <div className="min-w-0">
            <h3 className="font-serif-display text-xl text-[#1C1B1A]">Admin · Google Cloud Setup</h3>
            <p className="text-xs text-[#2C2B2A] mt-0.5">
              Do this ONE time. Every My Silo user connects their Google Calendar through this shared client — no per-user setup ever.
            </p>
          </div>
        </div>
        <button onClick={() => setCollapsed((v) => !v)} data-testid="admin-google-cloud-collapse"
          className="text-xs text-[#4A5D4E] hover:text-[#1C1B1A] flex items-center gap-1 shrink-0">
          {collapsed ? <><CaretDown size={12} /> Expand</> : <><CaretUp size={12} /> Collapse</>}
        </button>
      </header>

      {!collapsed && (
      <div className="p-5 space-y-5">
        {/* Live status card */}
        {configured ? (
          <div className="flex items-start gap-3 p-3 rounded-lg bg-[#EAF0EB] border border-[#4A5D4E]/30" data-testid="admin-gcal-status-configured">
            <ShieldCheck size={20} weight="fill" className="text-[#2F6B3A] shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-[#1C1B1A]">Shared client is active</div>
              <div className="text-[11px] text-[#2C2B2A] mt-0.5 font-mono break-all">{status.client_id_masked}</div>
              {status.updated_by_email && (
                <div className="text-[10px] text-[#2C2B2A] mt-1">Saved by {status.updated_by_email} · {new Date(status.updated_at).toLocaleString()}</div>
              )}
            </div>
            <button onClick={remove} data-testid="admin-gcal-clear"
              className="text-[11px] text-[#C16E5A] hover:text-[#8A4E3F] underline shrink-0 self-start">Clear</button>
          </div>
        ) : (
          <div className="flex items-start gap-3 p-3 rounded-lg bg-[#FBEBE4] border border-[#E8D5CF]" data-testid="admin-gcal-status-empty">
            <WarningCircle size={20} weight="fill" className="text-[#C16E5A] shrink-0 mt-0.5" />
            <div className="text-sm text-[#1C1B1A]">
              <strong className="block">No shared client saved yet.</strong>
              Every user will get a "Google Calendar not available" toast when they hit Connect. Do the 6 steps below, then paste the Client ID + Secret into the form here.
            </div>
          </div>
        )}

        {/* Test result banner */}
        {testResult && testResult.ok && (
          <div className="flex items-start gap-2 p-3 rounded-lg bg-[#EAF0EB] border border-[#4A5D4E]/30" data-testid="admin-gcal-test-ok">
            <CheckCircle size={18} weight="fill" className="text-[#2F6B3A] shrink-0 mt-0.5" />
            <div className="text-sm text-[#1C1B1A]">
              <strong className="block">Google approved.</strong>
              <span className="text-xs">{testResult.message}</span>
            </div>
          </div>
        )}
        {testResult && !testResult.ok && (
          <div className="flex items-start gap-2 p-3 rounded-lg bg-[#FBEBE4] border border-[#E8D5CF]" data-testid="admin-gcal-test-fail">
            <WarningCircle size={18} weight="fill" className="text-[#C16E5A] shrink-0 mt-0.5" />
            <div className="text-xs text-[#1C1B1A] whitespace-pre-wrap leading-relaxed">{testResult.error}</div>
          </div>
        )}

        {/* Redirect URI reminder — always visible so admin can copy any time */}
        <div className="bg-[#F0F1EE] rounded-lg p-3 border hairline">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#2C2B2A] mb-1.5">The redirect URI you'll paste in Google Cloud Console</div>
          <div className="flex items-center gap-2">
            <code className="text-[11px] font-mono text-[#1C1B1A] break-all flex-1 select-all">{redirectUri}</code>
            <button onClick={() => copy(redirectUri, "Redirect URI copied.")} data-testid="admin-gcal-copy-uri"
              className="p-1.5 rounded text-[#2C2B2A] hover:text-[#1C1B1A] hover:bg-white shrink-0" title="Copy">
              <Copy size={14} />
            </button>
          </div>
          <div className="text-[10px] text-[#2C2B2A] mt-1.5 italic">
            This box always shows the correct URI for the site you're on right now. When you deploy to a live domain, come back here from that live URL — the box updates automatically, and you add that second URI to the same OAuth client.
          </div>
        </div>

        {/* The 6-step walkthrough — collapsed by default, expand each one as needed */}
        <div className="space-y-2">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#2C2B2A]">Setup steps · expand each in order</div>

          <Step num={1} title="Create a Google Cloud project">
            <p>Open <Ext href="https://console.cloud.google.com/projectcreate">console.cloud.google.com/projectcreate</Ext> → give it a name like "My Silo Calendar" → Create. Wait ~15 seconds for it to spin up.</p>
          </Step>

          <Step num={2} title="Enable the Google Calendar API">
            <p>Open <Ext href="https://console.cloud.google.com/apis/library/calendar-json.googleapis.com">Calendar API library</Ext> → make sure the project selector (top of page) shows the project you just created → tap <strong>Enable</strong>. Takes 2 seconds.</p>
          </Step>

          <Step num={3} title="Configure the OAuth consent screen (Audience + Test users)">
            <p>Open <Ext href="https://console.cloud.google.com/auth/audience">console.cloud.google.com/auth/audience</Ext></p>
            <div className="flex items-start gap-2 bg-[#FBEBE4] border-l-2 border-[#C16E5A] rounded-r-md p-2.5 my-1">
              <DeviceMobile size={13} weight="duotone" className="text-[#C16E5A] shrink-0 mt-0.5" />
              <span className="text-[11px] leading-relaxed">
                <strong>On mobile:</strong> Google's console sidebar hides behind the hamburger menu — direct link above jumps straight to the Audience tab, skipping the "which sidebar item is Audience" hunt.
              </span>
            </div>
            <p><strong>User type: External</strong> · <strong>Publishing status: Testing</strong> is fine to start. Under <strong>Test users</strong>, tap "+ Add users" and add YOUR email (+ any family Gmails that'll connect their own calendars). 100-user cap in Testing mode is way more than a family needs.</p>
            <div className="flex items-start gap-2 bg-[#F0F1EE] border-l-2 border-[#4A5D4E] rounded-r-md p-2.5">
              <Info size={13} weight="duotone" className="text-[#4A5D4E] shrink-0 mt-0.5" />
              <span className="text-[11px]"><strong>To lift the 100-user cap later:</strong> hit "Publish app" on that same page. Users will see a small "Google hasn't verified this app" warning until you complete formal verification (privacy policy + terms + demo video → weeks of Google review). Family launches are fine unverified.</span>
            </div>
          </Step>

          <Step num={4} title="Create the OAuth Client ID (Web application)">
            <p>Open <Ext href="https://console.cloud.google.com/apis/credentials">console.cloud.google.com/apis/credentials</Ext> → <strong>+ Create Credentials → OAuth client ID</strong> → Application type <strong>Web application</strong>. Name field is a cosmetic label ("My Silo Calendar").</p>
            <div className="flex items-start gap-2 bg-[#F0F1EE] border-l-2 border-[#4A5D4E] rounded-r-md p-2.5 my-1">
              <span className="text-[#4A5D4E] text-[10px] uppercase tracking-[0.2em] font-medium shrink-0 mt-0.5">Skip</span>
              <span className="text-[11px]"><strong>Authorized JavaScript origins</strong> — leave EMPTY. My Silo doesn't use it. Only the "Authorized redirect URIs" section matters.</span>
            </div>
            <p>Scroll to <strong>Authorized redirect URIs</strong> → "+ Add URI" → paste the URI from the "The redirect URI you'll paste" box above → Save. Wait ~5 min for Google to propagate.</p>
          </Step>

          <Step num={5} title="Grab the Client ID + Client Secret">
            <p>Right after saving the client, Google shows a popup with both values. Client ID ends in <code className="font-mono text-[10px]">.apps.googleusercontent.com</code>; Client Secret starts with <code className="font-mono text-[10px]">GOCSPX-</code>.</p>
            <div className="flex items-start gap-2 bg-[#FBEBE4] border-l-2 border-[#C16E5A] rounded-r-md p-2.5 my-1">
              <DeviceMobile size={13} weight="duotone" className="text-[#C16E5A] shrink-0 mt-0.5" />
              <span className="text-[11px] leading-relaxed">
                <strong>iPhone / mobile gotcha:</strong> the popup can look empty — the secret sits off-screen right. Look for the tiny <Eye size={11} weight="duotone" className="inline align-[-1px] mx-0.5" /> <strong>eye icon at the top-right of the credential row</strong> and tap it to reveal. Long-press the value to copy.
              </span>
            </div>
            <div className="flex items-start gap-2 bg-[#F0F1EE] border-l-2 border-[#4A5D4E] rounded-r-md p-2.5">
              <span className="text-[#4A5D4E] text-[10px] uppercase tracking-[0.2em] font-medium shrink-0 mt-0.5">Missed it?</span>
              <span className="text-[11px] leading-relaxed">
                Client Secret is shown ONCE. If you lost it, don't delete the whole client — open your OAuth client's edit page → scroll to <strong>Client secrets</strong> → <strong>+ Add secret</strong> → copy the fresh <code className="font-mono">GOCSPX-…</code> IMMEDIATELY → then delete the old secret. Your Client ID stays the same.
              </span>
            </div>
          </Step>

          <Step num={6} title="Paste + Save + Test — done">
            <p>Paste the Client ID and Secret into the form below → <strong>Save shared client</strong>. Then hit <strong>Test with Google</strong> — the backend fires a fake code at Google's token endpoint using your saved values and tells you exactly what's missing (usually nothing) or gives you a green check.</p>
            <p>After the test is green, every My Silo user's "Connect Google Calendar" button just works — no Google Cloud Console for them.</p>
          </Step>
        </div>

        {/* The save form */}
        <div className="space-y-2 pt-2 border-t hairline">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#2C2B2A]">Paste your shared client credentials</div>
          <input value={cid} onChange={(e) => setCid(e.target.value)} data-testid="admin-gcal-client-id-input"
            autoComplete="off" placeholder="Client ID — ends in .apps.googleusercontent.com"
            className="w-full bg-white border hairline rounded-md px-3 py-2 font-mono text-xs focus:outline-none focus:ring-1 focus:ring-[#4A5D4E]" />
          <div className="flex gap-2 items-stretch">
            <input type="password" value={secret} onChange={(e) => setSecret(e.target.value)} data-testid="admin-gcal-client-secret-input"
              autoComplete="off" placeholder="Client Secret — starts with GOCSPX-"
              className="flex-1 bg-white border hairline rounded-md px-3 py-2 font-mono text-xs focus:outline-none focus:ring-1 focus:ring-[#4A5D4E]" />
            <button onClick={save} disabled={!cid.trim() || !secret.trim() || saving} data-testid="admin-gcal-save"
              className="text-xs px-4 py-2 rounded-md bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] disabled:opacity-60 flex items-center gap-1">
              <FloppyDisk size={12} weight="duotone" /> {saving ? "Saving…" : "Save shared client"}
            </button>
          </div>
          {configured && (
            <button onClick={test} disabled={testing} data-testid="admin-gcal-test"
              className="w-full text-xs px-4 py-2 rounded-md border-2 border-[#4A5D4E] text-[#4A5D4E] hover:bg-[#4A5D4E] hover:text-white disabled:opacity-60 flex items-center justify-center gap-1.5 font-medium">
              <CheckCircle size={13} weight="duotone" /> {testing ? "Asking Google…" : "Test with Google"}
            </button>
          )}
        </div>
      </div>
      )}
    </section>
  );
}
